import { diagnosticLog } from '../utils/diagnosticLogger';

function normalizeDesktopOsToken(raw: unknown): 'macos' | 'windows' | 'linux' | null {
  const p = String(raw ?? '').trim().toLowerCase();
  if (p === 'macos' || p === 'darwin' || p === 'mac') return 'macos';
  if (p === 'windows' || p === 'win32' || p === 'win') return 'windows';
  if (p === 'linux') return 'linux';
  return null;
}

function isWindowsConnectorHostInline(): boolean {
  if (typeof window === 'undefined') return false;
  const cached =
    normalizeDesktopOsToken((window as any).__MAESTRO_DESKTOP_OS__)
    ?? normalizeDesktopOsToken((window as any)?.__MAESTRO_CONNECTOR__?.platform);
  if (cached === 'windows') return true;
  if (cached === 'macos' || cached === 'linux') return false;
  if (!('__TAURI__' in window)) return false;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/Windows/i.test(ua)) return true;
  const plat = typeof navigator !== 'undefined' ? navigator.platform || '' : '';
  return /Win/i.test(plat);
}

function isDesktopMacOSUserAgentInline(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/(iPhone|iPad|iPod)/i.test(ua)) return false;
  return /Mac OS X|Macintosh/i.test(ua);
}

function isHttpsCloudShell(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location?.protocol !== 'https:') return false;
  const host = window.location?.hostname || '';
  return host.includes('maestrodesktop.com')
    || host.includes('staging.maestrodesktop.com')
    || host.includes('pages.dev')
    || host.includes('maestromusic.app');
}

/** macOS-only degraded IPC transport (#272, #275). When in doubt, false — never break Windows/Linux. */
export function isMacOsDegradedIpcTransportHost(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('__TAURI__' in window)) return false;

  const cached =
    normalizeDesktopOsToken((window as any).__MAESTRO_DESKTOP_OS__)
    ?? normalizeDesktopOsToken((window as any)?.__MAESTRO_CONNECTOR__?.platform);
  if (cached === 'windows' || cached === 'linux') return false;
  if (cached === 'macos') return true;

  if (isWindowsConnectorHostInline()) return false;

  // UA fallback only on HTTPS cloud shell — never on localhost/test harness (#275).
  if (isHttpsCloudShell() && isDesktopMacOSUserAgentInline()) return true;

  return false;
}

/** macOS transport banner mode (#272, #275, #309). */
export type TransportBannerKind = 'none' | 'relay_quiet' | 'broken_loud';

/** User-visible transport banner — relay-healed quiet note or broken loud warning. */
let _transportBannerKind: TransportBannerKind = 'none';
const listeners = new Set<() => void>();

export function getTransportBannerKind(): TransportBannerKind {
  return _transportBannerKind;
}

/** True when the loud broken-transport banner should show (#309). */
export function isTransportDegradedActive(): boolean {
  return _transportBannerKind === 'broken_loud';
}

/** True when the quiet cloud-relay note should show (#309). */
export function isTransportRelayQuietNoteActive(): boolean {
  return _transportBannerKind === 'relay_quiet';
}

export function subscribeTransportDegraded(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function notifyListeners(): void {
  for (const fn of listeners) fn();
}

export function isCloudRelayTransportConfig(cfg: IpcTransportConfig | null | undefined): boolean {
  return !!cfg?.cloud_relay_enabled;
}

/** Tier-1 local HTTP spine available (#248 B2). */
export function isLocalTransportSpineReady(cfg: IpcTransportConfig | null | undefined): boolean {
  if (!cfg?.local_transport_enabled || !cfg.token?.trim()) return false;
  const port = cfg.port ?? cfg.proxy_port ?? 0;
  return port > 0;
}

/** True when tier-5 cloud relay was the last successful transport dispatch (#248 B5). */
let _transportRelayLastResortUsed = false;

export function wasTransportRelayLastResortUsed(): boolean {
  return _transportRelayLastResortUsed;
}

export function markTransportRelayLastResortUsed(): void {
  _transportRelayLastResortUsed = true;
}

export function resetTransportRelayLastResortForTests(): void {
  _transportRelayLastResortUsed = false;
}

/** Fleet beacon — transport dispatch tier (#248 A3 / B3). */
export function logTransportPathUsed(tier: number, command: string): void {
  const line = `[Transport] transport_path_used tier=${tier} command=${command}`;
  console.info(line);
  diagnosticLog('info', 'transport', 'transport_path_used', {
    tier,
    command,
  });
}

/** In-memory sliding-window cap — per command + reason (#414 C). */
const beaconCapTimestamps = new Map<string, number[]>();

function beaconCapWindowMs(command: string): number {
  return (command === 'get_volume' || command === 'get_transport_info') ? 300_000 : 60_000;
}

function beaconCapKey(reason: string, command: string): string {
  return `${reason}:${command}`;
}

function pruneBeaconCap(key: string, windowMs: number, now: number): number[] {
  const kept = (beaconCapTimestamps.get(key) ?? []).filter((t) => now - t < windowMs);
  if (kept.length) beaconCapTimestamps.set(key, kept);
  else beaconCapTimestamps.delete(key);
  return kept;
}

function shouldEmitTransportBeacon(reason: string, command: string): boolean {
  const windowMs = beaconCapWindowMs(command);
  const key = beaconCapKey(reason, command);
  const now = Date.now();
  const recent = pruneBeaconCap(key, windowMs, now);
  if (recent.length >= 1) {
    logRelayLastResortSuppressed(reason, command);
    return false;
  }
  beaconCapTimestamps.set(key, [...recent, now]);
  return true;
}

/** Fleet counter when a capped beacon is dropped (#414 C). */
export function logRelayLastResortSuppressed(reason: string, command: string): void {
  const line = `[Transport] relay_last_resort_suppressed reason=${reason} command=${command}`;
  console.info(line);
  diagnosticLog('info', 'transport', 'relay_last_resort_suppressed', {
    reason,
    command,
  });
}

/** Reset beacon cap state (tests only). */
export function resetTransportBeaconCapForTests(): void {
  beaconCapTimestamps.clear();
}

/** Fleet beacon — Rust command Err surfaced without relay (#414 A). */
export function logTransportCommandFailed(tier: number, command: string, error: string): void {
  if (!shouldEmitTransportBeacon('transport_command_failed', command)) return;
  const line = `[Transport] transport_command_failed tier=${tier} command=${command} error=${error}`;
  console.warn(line);
  diagnosticLog('warn', 'transport', 'transport_command_failed', {
    tier,
    command,
    error,
  });
}

/** Fleet beacon — cloud relay is the last resort (#248 A3 / B3). */
export function logRelayLastResort(reason: string, command: string): void {
  if (!shouldEmitTransportBeacon(reason, command)) return;
  const line = `[Transport] relay_last_resort reason=${reason} command=${command}`;
  console.warn(line);
  diagnosticLog('warn', 'transport', 'relay_last_resort', {
    reason,
    command,
  });
}

/** Quiet one-time note — cloud fallback in use; playback may still work (#248 B4). */
export function notifyTransportRelayHealed(extra?: Record<string, unknown>): void {
  if (!isMacOsDegradedIpcTransportHost()) return;
  if (_transportBannerKind === 'broken_loud') return;
  if (_transportBannerKind === 'relay_quiet') return;
  _transportBannerKind = 'relay_quiet';
  console.info('[connector] transport_cloud_fallback — tier-5 relay in use (#248 B4)');
  diagnosticLog('info', 'protocol', 'transport_cloud_fallback', {
    transport_degraded: 'cloud_relay',
    transport_cloud_fallback: true,
    ...extra,
  });
  notifyListeners();
}

/** Local HTTP spine healthy — clear degraded / relay banners (#248 B4). */
export function notifyTransportLocalSpineHealthy(extra?: Record<string, unknown>): void {
  if (!isMacOsDegradedIpcTransportHost()) return;
  if (_transportBannerKind === 'none') return;
  _transportBannerKind = 'none';
  console.info('[connector] transport_local_spine_healthy — tier-1 local HTTP (#248 B4)');
  diagnosticLog('info', 'protocol', 'transport_local_spine_healthy', {
    transport_degraded: 'none',
    transport_local_spine_healthy: true,
    ...extra,
  });
  notifyListeners();
}

/** LOUD beacon + UI flag — no working transport fallback (#272, #309). */
export function notifyTransportDegradedActive(reason: string, extra?: Record<string, unknown>): void {
  if (!isMacOsDegradedIpcTransportHost()) return;
  if (_transportBannerKind === 'broken_loud') return;
  _transportBannerKind = 'broken_loud';
  console.warn('[connector] transport_degraded_active — no working fallback (#309)', reason);
  diagnosticLog('warn', 'protocol', 'transport_degraded_active', {
    transport_degraded: extra?.transport_degraded ?? 'postMessage',
    transport_degraded_active: true,
    reason,
    ...extra,
  });
  notifyListeners();
}

/** Reset transport banner state (tests only). */
export function resetTransportDegradedForTests(): void {
  _transportBannerKind = 'none';
  resetTransportRelayLastResortForTests();
  resetTransportBeaconCapForTests();
  notifyListeners();
}

export interface IpcTransportConfig {
  /** Live stream-proxy port for tier-1 local HTTP invoke (#248 A1). */
  port?: number;
  /** Back-compat alias for `port`. */
  proxy_port?: number;
  /** Per-boot Bearer token for POST /api/transport/invoke (#248 A1). */
  token?: string | null;
  force_post_message: boolean;
  macos_product_version?: string | null;
  platform?: string;
  /** Cloud relay is tier 5 only — never primary (#248 A3). */
  cloud_relay_enabled?: boolean;
  /** Local HTTP spine available (port bound + session token minted). */
  local_transport_enabled?: boolean;
  machine_id?: string;
  /** Optional tier-5 relay token — not required for local LAN play (#248 A4). */
  relay_token?: string | null;
}

export interface IpcTransportProbeResult {
  ok?: boolean;
  probe_id?: string;
  force_post_message?: boolean;
}

/** Client-side fetch shim — mirrors Connector `FORCE_POSTMESSAGE_IPC_JS`. macOS only (#275). */
export function applyIpcFetchPatchIfNeeded(force = false): boolean {
  if (typeof window === 'undefined') return false;
  if (!isMacOsDegradedIpcTransportHost()) return false;
  if ((window as any).__MAESTRO_IPC_FORCE_POSTMESSAGE__ && !force) return true;
  if (!force && window.location?.protocol !== 'https:') return false;

  const orig = window.fetch;
  if (typeof orig !== 'function') return false;

  window.fetch = function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
    const url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : (input as Request)?.url || '';
    if (url.startsWith('ipc://') || url.startsWith('http://ipc.localhost')) {
      return Promise.reject(
        new TypeError('Maestro IPC: custom protocol blocked — postMessage fallback'),
      );
    }
    return orig.call(this, input, init);
  };
  (window as any).__MAESTRO_IPC_FORCE_POSTMESSAGE__ = true;
  return true;
}

/** Pre-#272 Connector — IPC transport config/probe commands not registered. */
export function isUnknownIpcTransportCommandError(err: unknown): boolean {
  const msg = String(err).toLowerCase();
  return msg.includes('unknown command')
    || msg.includes('not found')
    || msg.includes('get_ipc_transport_config')
    || msg.includes('ipc_transport_probe');
}

/** Tauri ACL denial — not a WebKit transport block (#272). */
export function isTauriAclDenied(err: unknown): boolean {
  const msg = String(err);
  return msg.includes('not allowed')
    && msg.includes('Permissions associated with this command');
}

/** True when an invoke error indicates WebKit blocked custom-protocol IPC. */
export function isIpcTransportBlockError(err: unknown): boolean {
  if (isTauriAclDenied(err)) return false;
  const msg = String(err);
  return msg.includes('Load failed')
    || msg.includes('insecure content')
    || msg.includes('ipc://')
    || msg.includes('postMessage fallback')
    || msg.includes('custom protocol blocked');
}

export function isIpcInvokeHangError(err: unknown): boolean {
  const msg = String(err).toLowerCase();
  return msg.includes('ipc transport probe timeout')
    || msg.includes('ipc transport config timeout')
    || msg.includes('ipc invoke timeout')
    || msg.includes('postMessage fallback');
}

/** After a playUri command timeout, treat renderer PLAYING/TRANSITIONING as success. */
export async function verifyRendererPlayingAfterTimeout(
  getTransportInfo: (rendererId: string) => Promise<string>,
  rendererId?: string | null,
): Promise<boolean> {
  if (!rendererId) return false;
  try {
    const raw = await getTransportInfo(rendererId);
    const upper = String(raw || '').toUpperCase();
    return upper.includes('PLAYING') || upper.includes('TRANSITIONING');
  } catch {
    return false;
  }
}
